import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { staffService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import {
  AdminPageHeader, Avatar, Btn, Card, Chip, DataTable, DarkCard, EmptyLine, Field, HardCard, InitialsBox,
  Loading, NoteCard, Pill, RING_CONIC, SubTabs, hhmm, initialsOf, inputClass, isoDate, relTime, useAdminShell,
} from '../../components/admin/shell';
import CapabilityGrid from '../../components/admin/CapabilityGrid';
import { CAPABILITIES, CAP_BY_KEY, countGrants, diffPills, diffSummary, normalizeGrants, sameGrants } from '../../utils/staffCapabilities';
import { activityColumns } from './AuditPage';

const roleNote = (role) => {
  const keys = Object.keys(normalizeGrants(role?.grants || {}));
  const words = ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
  const n = keys.length;
  return `${n <= 10 ? words[n] : n} section${n === 1 ? '' : 's'}: ${keys.map((k) => CAP_BY_KEY[k]?.label.toLowerCase()).join(', ')}. Editable cell by cell after they accept.`;
};

/**
 * PEOPLE › STAFF — spec/staff.md. Superadmin only. PEOPLE (who has a key, invites, the
 * person sheet with the capability grid and their activity) and ROLES (the default fills,
 * with propagation as a question, never a default).
 */
export default function StaffPage({ view = 'people' }) {
  const navigate = useNavigate();
  const { userId, roleKey } = useParams();
  const { isSuperadmin, currentUser } = useUser();
  const { toast } = useAdminShell();
  const [people, setPeople] = useState(null);
  const [roles, setRoles] = useState(null);
  const [filter, setFilter] = useState('all');
  const [invite, setInvite] = useState(false);
  const [newRole, setNewRole] = useState(false);

  const loadPeople = useCallback(async () => { const r = await staffService.people(); if (r.success) setPeople(r.data); }, []);
  const loadRoles = useCallback(async () => { const r = await staffService.roles(); if (r.success) setRoles(r.data); }, []);
  useEffect(() => { loadPeople(); loadRoles(); }, [loadPeople, loadRoles]);

  if (!isSuperadmin) return null;
  const sheetOpen = !!userId || !!roleKey || newRole;
  const sub = view === 'roles' ? 'ROLES' : 'PEOPLE';
  const others = (people || []).filter((p) => !p.isSuperadmin);
  const alone = people && others.length === 0;

  const header = (
    <>
      <AdminPageHeader
        section="staff"
        eyebrowTail={sub}
        meta={view === 'roles' ? 'What each role grants by default. Editing a role is a separate decision from editing a person.' : alone ? 'You are the only person with access.' : 'Everyone with a key, how they got it, and when they last used it.'}
        actions={sheetOpen ? null : view === 'roles' ? <Btn variant="white" onClick={() => setNewRole(true)}>+ NEW ROLE</Btn> : <Btn variant="white" onClick={() => setInvite(true)}>+ INVITE STAFF</Btn>}
      />
      <SubTabs
        tabs={[{ key: 'people', label: 'PEOPLE' }, { key: 'roles', label: 'ROLES' }]}
        active={view}
        onChange={(k) => { setNewRole(false); navigate(k === 'roles' ? '/admin/staff/roles' : '/admin/staff'); }}
        note={view === 'roles' ? 'Opening a role edits the default fill.' : 'Opening a person edits their cells only.'}
      />
    </>
  );

  if (view === 'roles') {
    return (
      <>
        {header}
        {roleKey || newRole ? (
          <RoleSheet roleKey={roleKey} roles={roles} isNew={newRole} onBack={() => { setNewRole(false); navigate('/admin/staff/roles'); }} onSaved={() => { setNewRole(false); loadRoles(); loadPeople(); navigate('/admin/staff/roles'); }} />
        ) : (
          <div className="flex flex-col gap-4">
            <DataTable
              columns={[
                { key: 'label', label: 'ROLE', fr: 1.4 },
                { key: 'sectionCount', label: 'SECTIONS', fr: 0.9, right: true },
                { key: 'verbCount', label: 'VERBS', fr: 0.8, right: true, render: (r) => (r.locked ? 'ALL' : r.verbCount) },
                { key: 'peopleCount', label: 'PEOPLE', fr: 0.9, right: true },
                { key: 'updatedAt', label: 'LAST CHANGED', fr: 1.1, render: (r) => (r.locked ? 'locked' : r.updatedAt ? isoDate(r.updatedAt) : 'built-in') },
                { key: 'updatedByName', label: 'BY', fr: 0.7, render: (r) => (r.updatedByName ? r.updatedByName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '—') },
              ]}
              rows={roles || []}
              loading={!roles}
              rowKey={(r) => r.key}
              hasActions
              actions={(r) => (r.locked ? [{ label: 'LOCKED', onClick: () => {}, variant: 'plain' }] : [{ label: 'OPEN', onClick: () => navigate(`/admin/staff/roles/${r.key}`) }])}
              footerLeft={`${(roles || []).length} roles`}
            />
            <NoteCard>
              A role is a starting fill, not a lock. Editing one never changes a person until you choose to apply it, and anyone whose cells have diverged keeps them. SUPERADMIN is not editable here — it is granted in the database, deliberately, so that no screen in the product can create another one.
            </NoteCard>
          </div>
        )}
      </>
    );
  }

  const rows = (people || []).filter((p) => filter === 'all' || p.status === filter);
  return (
    <>
      {header}
      {userId ? (
        <PersonSheet userId={userId} roles={roles} selfId={currentUser?.id} onBack={() => navigate('/admin/staff')} onChanged={loadPeople} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-[6px]">
            {['all', 'active', 'invited', 'suspended'].map((f) => <Chip key={f} selected={filter === f} onClick={() => setFilter(f)}>{f.toUpperCase()}</Chip>)}
          </div>
          <DataTable
            columns={[
              { key: 'name', label: 'NAME', fr: 1.8 },
              { key: 'email', label: 'EMAIL', fr: 1.9 },
              { key: 'presetWord', label: 'PRESET', fr: 1 },
              { key: 'sectionCount', label: 'SECTIONS', fr: 0.9, right: true },
              { key: 'lastUsedAt', label: 'LAST ACTIVE', fr: 1, render: (p) => (p.status === 'invited' ? 'never' : relTime(p.lastUsedAt)) },
              { key: 'invitedByName', label: 'INVITED BY', fr: 0.9, render: (p) => (p.invitedByName ? p.invitedByName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '—') },
              { key: 'status', label: 'STATUS', fr: 0.9, render: (p) => p.status.toUpperCase() },
            ]}
            rows={rows}
            loading={!people}
            rowKey={(p) => p.userId || p.inviteId}
            hasActions
            // A pending invite is not a person yet: it can be sent again (a fresh 7-day
            // link on the same role) or withdrawn.
            actions={(p) => (p.userId
              ? [{ label: 'OPEN', onClick: () => navigate(`/admin/staff/${p.userId}`) }]
              : [
                { label: 'RESEND', onClick: async () => { const r = await staffService.invite({ email: p.email, roleKey: p.roleKey }); if (r.success) toast(`INVITE SENT AGAIN TO ${p.email.toUpperCase()}`); loadPeople(); } },
                { label: 'WITHDRAW', variant: 'danger', onClick: async () => { await staffService.deleteInvite(p.inviteId); toast('INVITE WITHDRAWN'); loadPeople(); } },
              ])}
            footerLeft={`${(people || []).filter((p) => p.status !== 'invited').length} with access · ${(people || []).filter((p) => p.status === 'invited').length} invite pending`}
          />
          {alone && (
            <DarkCard className="flex flex-col items-start gap-3 p-7">
              <div className="whitespace-nowrap font-mono text-[16px] font-bold uppercase tracking-[0.04em]">NOBODY ELSE HAS A KEY.</div>
              <p className="font-mono text-[12px] leading-[1.6] text-[#BDBDBD]">Invite someone by email and pick a role. They get the sections that role covers and nothing else, and you can change any cell afterwards. Superadmin is not on the list of roles — it is granted in the database, on purpose.</p>
              <Btn variant="white" onClick={() => setInvite(true)}>+ INVITE STAFF</Btn>
            </DarkCard>
          )}
        </div>
      )}
      {invite && <InviteModal roles={roles || []} onClose={() => setInvite(false)} onSent={(row) => {
        setInvite(false);
        loadPeople();
        // An existing account has its key already: open their sheet so the role and cells get set now.
        if (row?.userId) navigate(`/admin/staff/${row.userId}`);
      }} />}
    </>
  );
}

function InviteModal({ roles, onClose, onSent }) {
  const editable = roles.filter((r) => !r.locked);
  const [mode, setMode] = useState('existing');
  const [email, setEmail] = useState('');
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [picked, setPicked] = useState(null);
  const [roleKey, setRoleKey] = useState(editable[0]?.key || null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const role = editable.find((r) => r.key === roleKey);

  // Lookup — debounced, real accounts only; people who already hold a key are shown greyed.
  useEffect(() => {
    if (mode !== 'existing') return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    const t = setTimeout(async () => {
      const r = await staffService.lookup(term);
      setResults(r.success ? (r.data || []) : []);
    }, 250);
    return () => clearTimeout(t);
  }, [q, mode]);

  const ready = mode === 'existing' ? !!picked : !!email.trim();
  const send = async () => {
    setBusy(true); setError('');
    const r = await staffService.invite(mode === 'existing' ? { userId: picked.userId, roleKey } : { email: email.trim(), roleKey });
    setBusy(false);
    if (r.success) onSent(r.data); else setError(r.error || 'Could not send the invite.');
  };
  const nameOf = (u) => u.displayName || u.email;

  return (
    <div className="fixed inset-0 safe-inset z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-16" onClick={onClose}>
      <HardCard className="w-full max-w-[560px]" onClick={(e) => e.stopPropagation()}>
        <div onClick={(e) => e.stopPropagation()}>
          <div className="px-6 pb-2 pt-6 font-mono text-[14px] font-bold uppercase tracking-[0.14em]">INVITE STAFF</div>
          <div className="flex flex-col gap-4 p-6 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <Chip selected={mode === 'existing'} onClick={() => { setMode('existing'); setError(''); }} className="text-[10px] tracking-[0.1em] px-[11px]">EXISTING USER</Chip>
              <Chip selected={mode === 'email'} onClick={() => { setMode('email'); setError(''); }} className="text-[10px] tracking-[0.1em] px-[11px]">BY EMAIL</Chip>
              <span className="font-mono text-[11px] text-[#6B7280]">{mode === 'existing' ? 'Someone already on Portal. The key is theirs the moment you add them.' : 'Someone without an account yet. The key attaches when they sign up.'}</span>
            </div>

            {mode === 'existing' ? (
              <Field label="WHO" error={error}>
                {picked ? (
                  <div className="flex items-center gap-3 rounded-full bg-[#F7F7F7] py-2 pl-2 pr-3">
                    <InitialsBox initials={initialsOf(nameOf(picked))} name={nameOf(picked)} size={28} />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-mono text-[12px] font-bold">{nameOf(picked)}</span>
                      <span className="truncate font-mono text-[10px] text-[#6B7280]">{picked.handle ? `@${picked.handle} · ` : ''}{picked.email}</span>
                    </span>
                    <Btn size="row" className="ml-auto" onClick={() => { setPicked(null); setQ(''); setResults(null); }}>CHANGE</Btn>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, handle or email…" className={`${inputClass} text-[13px]`} autoFocus aria-label="Search people" />
                    {results && (
                      <div className="max-h-[260px] overflow-y-auto rounded-[22px] bg-[#F7F7F7] p-2">
                        {results.length === 0 ? (
                          <p className="px-3 py-3 font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">NO ONE MATCHES. TRY BY EMAIL.</p>
                        ) : results.map((u) => (
                          <button key={u.userId} type="button" disabled={u.hasKey} onClick={() => { setPicked(u); setError(''); }} className="flex w-full items-center gap-3 rounded-[16px] px-3 py-2 text-left hover:bg-white disabled:cursor-not-allowed disabled:opacity-40">
                            <InitialsBox initials={initialsOf(nameOf(u))} name={nameOf(u)} size={24} />
                            <span className="flex min-w-0 flex-col">
                              <span className="truncate font-mono text-[12px] font-bold">{nameOf(u)}</span>
                              <span className="truncate font-mono text-[10px] text-[#6B7280]">{u.handle ? `@${u.handle} · ` : ''}{u.email}</span>
                            </span>
                            {u.hasKey && <span className="ml-auto font-mono text-[9px] font-bold tracking-[0.1em] text-[#6B7280]">HAS A KEY</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Field>
            ) : (
              <Field label="EMAIL" error={error}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputClass} text-[13px]`} autoFocus /></Field>
            )}

            <Field label="ROLE">
              <div className="flex flex-wrap gap-2">
                {editable.map((r) => <Chip key={r.key} selected={roleKey === r.key} onClick={() => setRoleKey(r.key)} className="text-[10px] tracking-[0.1em] px-[11px]">{r.label}</Chip>)}
              </div>
            </Field>
            {role && <p className="font-mono text-[11px] leading-[1.6] text-[#6B7280]">{roleNote(role)}</p>}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#F2F2F2] pt-4">
              <span className="font-mono text-[10px] tracking-[0.1em] text-[#6B7280]">{mode === 'existing' ? 'OPENS THEIR SHEET NEXT SO YOU CAN SET THE CELLS' : 'LINK EXPIRES IN 7 DAYS'}</span>
              <div className="flex gap-2">
                <Btn size="sheet" onClick={onClose}>CANCEL</Btn>
                <Btn size="sheet" variant="solid" onClick={send} disabled={busy || !ready}>{busy ? (mode === 'existing' ? 'ADDING…' : 'SENDING…') : mode === 'existing' ? 'GIVE THEM A KEY' : 'SEND INVITE'}</Btn>
              </div>
            </div>
          </div>
        </div>
      </HardCard>
    </div>
  );
}

function PersonSheet({ userId, roles, selfId, onBack, onChanged }) {
  const { showConfirm, showAlert } = useDialog();
  const { previewAs } = useUser();
  const [person, setPerson] = useState(null);
  const [draft, setDraft] = useState({});
  const [roleKey, setRoleKey] = useState(null);
  const [tab, setTab] = useState('access');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await staffService.person(userId);
    if (r.success) { setPerson(r.data); setDraft(r.data.grants || {}); setRoleKey(r.data.roleKey || null); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const editable = (roles || []).filter((r) => !r.locked);
  const role = editable.find((r) => r.key === roleKey);
  const divergedCells = useMemo(() => {
    if (!role) return 0;
    const a = normalizeGrants(role.grants); const b = normalizeGrants(draft);
    let n = 0;
    for (const c of CAPABILITIES) for (const v of c.verbs) if (!!a[c.key]?.includes(v) !== !!b[c.key]?.includes(v)) n += 1;
    return n;
  }, [role, draft]);
  const isCustom = !role || divergedCells > 0;
  const pills = person ? diffPills(person.grants, draft) : [];
  const counts = countGrants(draft);
  const readOnly = !person || person.isSuperadmin || person.userId === selfId;

  if (!person) return <Card><Loading /></Card>;

  const save = async () => {
    setBusy(true);
    const r = await staffService.updateGrants(userId, { roleKey: isCustom ? roleKey : roleKey, grants: normalizeGrants(draft) });
    setBusy(false);
    if (!r.success) { await showAlert({ title: 'COULD NOT SAVE', message: r.error, type: 'error', theme: 'admin' }); return; }
    setPerson(r.data); setDraft(r.data.grants || {}); onChanged?.();
  };
  const suspend = async () => {
    const suspended = person.status === 'suspended';
    const ok = suspended ? true : await showConfirm({ title: 'SUSPEND STAFF', message: `${person.name} keeps their key but every check fails until you reinstate them.`, confirmLabel: 'SUSPEND', variant: 'primary', theme: 'admin' });
    if (!ok) return;
    const r = suspended ? await staffService.reinstate(userId) : await staffService.suspend(userId);
    if (!r.success) await showAlert({ title: 'COULD NOT UPDATE', message: r.error, type: 'error', theme: 'admin' });
    load(); onChanged?.();
  };
  const remove = async () => {
    const ok = await showConfirm({ title: 'REMOVE STAFF', message: `${person.name} loses every section. Their activity rows stay in the log.`, confirmLabel: 'REMOVE STAFF', variant: 'danger', theme: 'admin' });
    if (!ok) return;
    const r = await staffService.remove(userId);
    if (!r.success) { await showAlert({ title: 'COULD NOT REMOVE', message: r.error, type: 'error', theme: 'admin' }); return; }
    onChanged?.(); onBack();
  };

  return (
    <section className="flex flex-col gap-[14px]">
      <DarkCard className="flex flex-col gap-4 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            <span className="relative h-[64px] w-[64px] flex-shrink-0">
              <span className="absolute inset-0 rounded-full" style={{ background: RING_CONIC }} />
              <span className="absolute inset-[3px] overflow-hidden rounded-full border-[3px] border-[#0A0A0A] bg-[#1F1F1F]">
                <Avatar url={person.avatarUrl} initials={initialsOf(person.name || person.email)} name={person.name} size={58} className="bg-transparent text-white" />
              </span>
            </span>
            <div className="flex min-w-0 flex-col gap-[5px] pt-1">
              <span className="flex flex-wrap items-center gap-2">
                <Pill tone={person.status === 'suspended' ? 'bad' : person.status === 'invited' ? 'warn' : 'good'}>{String(person.status || '').toUpperCase()}</Pill>
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A8A]">
                  {person.isSuperadmin ? 'SUPERADMIN · EVERY SECTION' : `${isCustom ? 'CUSTOM' : (role?.label || 'CUSTOM')} · ${counts.sections} SECTIONS · ${counts.verbs} VERBS`}
                </span>
              </span>
              <span className="font-mono text-[22px] font-bold leading-[1.15]">{person.name || person.email}</span>
              <span className="truncate font-mono text-[11px] text-[#BDBDBD]">
                {person.email}{person.invitedByName ? ` · invited by ${person.invitedByName}${person.invitedAt ? ` ${isoDate(person.invitedAt)}` : ''}` : ''} · last active {relTime(person.lastUsedAt)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-[6px]">
            {!person.isSuperadmin && (
              <Btn variant="white" size="card" onClick={() => previewAs?.({ ...person, grants: person.grants })}>
                PREVIEW AS {(person.name || person.email || '').split(' ')[0].toUpperCase()}
              </Btn>
            )}
            {!readOnly && <Btn variant="quiet" size="card" onClick={suspend}>{person.status === 'suspended' ? 'REINSTATE' : 'SUSPEND'}</Btn>}
            {!readOnly && <Btn variant="danger" size="card" onClick={remove}>REMOVE STAFF</Btn>}
            <Btn variant="quiet" size="card" onClick={onBack}>BACK</Btn>
          </div>
        </div>
      </DarkCard>

      <SubTabs tabs={[{ key: 'access', label: 'ACCESS' }, { key: 'activity', label: 'ACTIVITY' }]} active={tab} onChange={setTab} />

      {tab === 'activity' ? (
        <PersonActivity userId={userId} />
      ) : person.isSuperadmin ? (
        <NoteCard>Everything, granted in the database. Not editable here, on purpose.</NoteCard>
      ) : (
        <Card bodyClass="px-3 pb-3">
          <div className="flex flex-wrap items-center gap-[6px] px-2 pb-3">
            <span className="pr-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">ROLE</span>
            {editable.map((r) => (
              <Chip key={r.key} selected={!isCustom && roleKey === r.key} onClick={() => { if (readOnly) return; setRoleKey(r.key); setDraft(normalizeGrants(r.grants)); }}>{r.label}</Chip>
            ))}
            <Chip selected={isCustom}>CUSTOM</Chip>
            <span className="flex-1 basis-[240px] font-mono text-[11px] leading-[1.5] text-[#6B7280]">
              {role && isCustom
                ? `Started as ${role.label}. ${divergedCells === 1 ? 'One cell diverges' : `${divergedCells} cells diverge`}, so the role now reads CUSTOM.`
                : role ? `${role.label}.` : ''}
            </span>
          </div>
          <CapabilityGrid grants={draft} onChange={setDraft} readOnly={readOnly} />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#F2F2F2] px-2 pt-4">
            <span className="flex min-w-0 flex-1 basis-[280px] flex-wrap items-center gap-[6px]">
              {pills.length === 0 ? (
                <span className="font-mono text-[11px] text-[#9CA3AF]">NOTHING CHANGED YET</span>
              ) : pills.map((d) => (
                <Pill key={d.label} tone={d.added ? 'good' : 'bad'}>{d.sign} {d.label}</Pill>
              ))}
            </span>
            {!readOnly && pills.length > 0 && (
              <span className="flex flex-shrink-0 gap-2">
                <Btn onClick={() => { setDraft(person.grants || {}); setRoleKey(person.roleKey || null); }}>DISCARD</Btn>
                <Btn variant="solid" onClick={save} disabled={busy}>{busy ? 'SAVING…' : 'SAVE ACCESS'}</Btn>
              </span>
            )}
          </div>
          {readOnly && person.userId === selfId && (
            <div className="px-2 pt-3 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[#9CA3AF]">YOUR OWN ROW IS READ-ONLY.</div>
          )}
        </Card>
      )}
    </section>
  );
}

function PersonActivity({ userId }) {
  const [sessions, setSessions] = useState(null);
  const [open, setOpen] = useState(new Set());
  useEffect(() => { (async () => { const r = await staffService.personSessions(userId, 20); setSessions(r.success ? r.data : []); })(); }, [userId]);
  if (!sessions) return <Card><Loading /></Card>;
  if (sessions.length === 0) return <Card bodyClass="px-3 pb-3"><EmptyLine>NO SESSIONS YET.</EmptyLine></Card>;
  const countLine = (s) => [['VIEWS', s.byKind?.view], ['SEARCHES', s.byKind?.search], ['WRITES', s.byKind?.write], ['DENIED', s.byKind?.denied], ['EXPORTS', s.byKind?.export]].filter(([, n]) => n).map(([l, n]) => `${n} ${l}`).join(' · ');
  return (
    <div className="flex flex-col gap-[14px]">
      {sessions.map((s, i) => {
        const isOpen = open.has(s.sessionId) || i === 0;
        return (
          <div key={s.sessionId} className="flex flex-col gap-2 rounded-[24px] bg-white p-3">
            <button type="button" onClick={() => setOpen((o) => { const n = new Set(o); if (n.has(s.sessionId)) n.delete(s.sessionId); else n.add(s.sessionId); return n; })} className="flex w-full flex-wrap items-center justify-between gap-3 rounded-[18px] px-3 py-3 text-left hover:bg-[#F7F7F7]">
              <span className="font-mono text-[11px] font-bold uppercase tracking-[0.12em]">{isoDate(s.startedAt)} · SIGNED IN {hhmm(s.startedAt)} · {s.actionCount} ACTIONS · {s.endedHow ? (s.endedHow === 'signed out' ? `SIGNED OUT ${hhmm(s.endedAt)}` : 'EXPIRED') : 'STILL SIGNED IN'}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF]">{countLine(s) || 'NO ACTIONS'}</span>
            </button>
            {isOpen && (
              <DataTable columns={activityColumns({ withWho: false })} rows={s.rows.filter((r) => r.kind !== 'session')} rowKey={(r) => r.id} empty="NOTHING IN THIS SITTING." />
            )}
          </div>
        );
      })}
    </div>
  );
}

function RoleSheet({ roleKey, roles, isNew, onBack, onSaved }) {
  const { showAlert, showConfirm } = useDialog();
  const role = (roles || []).find((r) => r.key === roleKey);
  const [label, setLabel] = useState('');
  const [draft, setDraft] = useState({});
  const [startFrom, setStartFrom] = useState('blank');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (role) { setLabel(role.label); setDraft(role.grants || {}); } }, [role]);
  if (!isNew && !roles) return <Card><Loading /></Card>;
  if (!isNew && !role) return <Card bodyClass="px-3 pb-3"><EmptyLine>ROLE NOT FOUND.</EmptyLine></Card>;
  const counts = countGrants(draft);
  const unchanged = !isNew && sameGrants(role.grants, draft) && label === role.label;
  const diff = isNew ? (counts.verbs === 0 ? 'NOTHING SET YET · PICK A ROLE TO COPY, OR TICK CELLS' : diffSummary({}, draft)) : unchanged ? 'NOTHING CHANGED YET' : (diffSummary(role.grants, draft) || `RENAMED TO ${label}`);
  const pills = isNew ? [] : diffPills(role?.grants || {}, draft);
  const people = role?.peopleCount || 0;

  /** A custom role can go. Anyone still on it reads CUSTOM afterwards. */
  const removeRole = async () => {
    const ok = await showConfirm({
      title: 'DELETE ROLE',
      message: people > 0
        ? `${people} ${people === 1 ? 'person keeps' : 'people keep'} the cells they have; their role reads CUSTOM from now on.`
        : 'Nobody is on it, so nothing changes for anyone.',
      confirmLabel: 'DELETE ROLE',
      variant: 'danger',
      theme: 'admin',
    });
    if (!ok) return;
    const r = await staffService.deleteRole(role.key);
    if (!r.success) { await showAlert({ title: 'COULD NOT DELETE', message: r.error, type: 'error', theme: 'admin' }); return; }
    onSaved();
  };

  /**
   * The two-action confirm the handoff asks for: APPLY TO ALL re-fills everyone whose
   * cells still match the old role; ONLY NEW HIRES saves the role and leaves the people
   * who already have it alone. The diff pills sit right above the button that opened
   * this, so the sentence here only has to say who it lands on.
   */
  const confirmSave = async () => {
    if (isNew || people === 0) {
      const ok = await showConfirm({
        title: isNew ? `CREATE ${label || 'ROLE'}?` : `SAVE ${role.label}?`,
        message: `${diff}. Nobody is on this role yet, so there is nothing to apply.`,
        confirmLabel: isNew ? 'CREATE ROLE' : 'SAVE ROLE',
        variant: 'primary',
        theme: 'admin',
      });
      if (ok) save(false);
      return;
    }
    const diverged = role.divergedCount > 0
      ? ` ${role.divergedCount === 1 ? `${role.divergedNames[0]} has diverged cells and keeps them` : `${role.divergedCount} of them have diverged cells and keep them`} either way.`
      : '';
    const answer = await showConfirm({
      title: `APPLY TO EVERYONE ON ${role.label}?`,
      message: `${diff}. ${people} ${people === 1 ? 'person uses' : 'people use'} this role.${diverged}`,
      confirmLabel: `APPLY TO ALL ${people}`,
      secondLabel: 'ONLY NEW HIRES',
      cancelLabel: 'CANCEL',
      variant: 'primary',
      theme: 'admin',
    });
    if (answer === true) save(true);
    else if (answer === 'second') save(false);
  };

  const save = async (applyToAll) => {
    setBusy(true); setError('');
    const r = isNew ? await staffService.createRole({ label, grants: normalizeGrants(draft) }) : await staffService.saveRole(role.key, { label, grants: normalizeGrants(draft), applyToAll });
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save.'); await showAlert({ title: 'COULD NOT SAVE', message: r.error, type: 'error', theme: 'admin' }); return; }
    onSaved();
  };

  return (
    <section className="flex flex-col gap-[14px]">
      <DarkCard className="flex flex-wrap items-center justify-between gap-4 p-6">
        <div className="flex min-w-0 flex-col gap-[6px]">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A8A]">{counts.sections} SECTIONS · {counts.verbs} VERBS · {people} {people === 1 ? 'PERSON' : 'PEOPLE'}</span>
          <span className="whitespace-nowrap font-mono text-[22px] font-bold uppercase tracking-[0.04em]">{isNew ? (label || 'NEW ROLE') : role.label}</span>
          <span className="font-mono text-[11px] leading-[1.6] text-[#BDBDBD]">{isNew ? 'Nobody is on it yet. Pick a role to copy, or start from nothing.' : 'Changing it does not change anyone until you say so.'}</span>
        </div>
        <div className="flex flex-wrap gap-[6px]">
          {!isNew && role && !role.isBuiltin && !role.locked && (
            <Btn variant="danger" size="card" onClick={removeRole}>DELETE ROLE</Btn>
          )}
          <Btn variant="quiet" size="card" onClick={onBack}>BACK</Btn>
        </div>
      </DarkCard>
      {isNew && (
        <Card className="flex flex-wrap items-end gap-4" bodyClass="flex flex-wrap items-end gap-4 px-5">
          <Field label="ROLE NAME" error={error} className="min-w-[260px]"><input value={label} onChange={(e) => setLabel(e.target.value.toUpperCase())} placeholder="SUPPORT" className={`${inputClass} text-[13px] font-bold tracking-[0.05em] placeholder:text-[#9CA3AF]`} /></Field>
          <Field label="START FROM">
            <div className="flex flex-wrap gap-2">
              <Chip selected={startFrom === 'blank'} onClick={() => { setStartFrom('blank'); setDraft({}); }} className="text-[10px] tracking-[0.1em] px-[10px] py-[7px]">BLANK</Chip>
              {(roles || []).filter((r) => !r.locked).map((r) => <Chip key={r.key} selected={startFrom === r.key} onClick={() => { setStartFrom(r.key); setDraft(normalizeGrants(r.grants)); }} className="text-[10px] tracking-[0.1em] px-[10px] py-[7px]">{r.label}</Chip>)}
            </div>
          </Field>
        </Card>
      )}
      {!isNew && (
        <Card bodyClass="px-5">
          <Field label="ROLE NAME" className="max-w-[260px]"><input value={label} onChange={(e) => setLabel(e.target.value.toUpperCase())} className={`${inputClass} font-bold tracking-[0.05em]`} /></Field>
        </Card>
      )}
      <Card bodyClass="px-3 pb-3">
        <CapabilityGrid grants={draft} onChange={setDraft} />
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#F2F2F2] px-2 pt-4">
          <span className="flex min-w-0 flex-1 basis-[280px] flex-wrap items-center gap-[6px]">
            {pills.length === 0 ? <span className="font-mono text-[11px] text-[#9CA3AF]">{diff}</span> : pills.map((d) => <Pill key={d.label} tone={d.added ? 'good' : 'bad'}>{d.sign} {d.label}</Pill>)}
          </span>
          {(isNew ? label.trim().length >= 2 && counts.verbs > 0 : !unchanged) && (
            <span className="flex flex-shrink-0 gap-2">
              {!isNew && <Btn onClick={() => { setLabel(role.label); setDraft(role.grants || {}); }}>DISCARD</Btn>}
              <Btn variant="solid" onClick={confirmSave} disabled={busy}>{busy ? 'SAVING…' : isNew ? 'CREATE ROLE' : 'SAVE ROLE'}</Btn>
            </span>
          )}
        </div>
      </Card>
    </section>
  );
}
